/**
 * The MDX transforms Docusaurus and Mintlify both need.
 *
 * Both platforms write MDX: Markdown with JSX components and JavaScript
 * statements in it. Our pipeline is Markdown, not MDX, and that difference
 * causes the same four problems on both platforms — so they are solved once,
 * here, and the platform files hold only what is genuinely platform-specific.
 *
 * The governing decision, which applies to all four: MDX IS CODE, AND WE DO NOT
 * RUN CODE. Every transform below either converts the construct into something
 * our schema models, or preserves it in a form that renders as content and
 * reports it. Nothing is deleted, and nothing is left in a state where the
 * reader is shown source text.
 */
import { dedent, quoteAttr } from './import-notes.js';
import { snippetNameFromPath } from './mintlify.js';
import { fenceWidth, replaceJsxElements, replaceOutsideCode, sourceLines, } from './import-scan.js';
/**
 * An MDX import of a DOCUMENT, which is an include however it is spelled.
 *
 * `import Warning from '/snippets/warning.mdx'` and then `<Warning />` is how
 * Mintlify and Docusaurus both write a shared block in MDX — Docusaurus calls
 * them partials and names them `_thing.mdx`. It was reported and not converted,
 * on the reasoning that resolving an import means evaluating a module.
 *
 * It does not, when the specifier names a Markdown file. A `.md` or `.mdx`
 * import is a document, `<X />` is where that document goes, and both are
 * facts on the page rather than guesses about a module. A specifier that names
 * anything else — `@theme/Tabs`, `../components/Chart` — is still a component
 * this product cannot run, and is still reported rather than guessed at.
 *
 * The distinction is the extension, and it is the whole safety of this: a
 * component import cannot end in `.mdx` and a document import cannot not.
 */
const DOCUMENT_IMPORT = /^import\s+([A-Z][A-Za-z0-9_]*)\s+from\s+['"]([^'"]+\.mdx?)['"];?\s*$/;
/** Local name to specifier, for every import that names a document. */
export function mdxDocumentImports(text) {
    const found = new Map();
    for (const line of sourceLines(text)) {
        if (line.inCode)
            continue;
        const match = DOCUMENT_IMPORT.exec(line.text.trim());
        if (match)
            found.set(match[1], match[2]);
    }
    return found;
}
/** Every document a page imports, for the collector that carries them in. */
export function mdxSnippetPaths(source) {
    const imports = mdxDocumentImports(source);
    const used = new Set();
    for (const [name, path] of imports) {
        // Imported and never used is a leftover, not an include: carrying the file
        // would put a page's worth of words in the export for nothing.
        if (new RegExp(`<${name}\\s*/?>`).test(source))
            used.add(path);
    }
    return [...used];
}
/**
 * Turn those imports and their uses into shared blocks.
 *
 * Both halves or neither: the reference becomes `::snippet` and the import
 * statement goes, because a statement whose component is now a directive is a
 * line about nothing. An import that is never used keeps its statement and is
 * reported with the rest — it says what the page depended on, which is what
 * the comment is for.
 */
export function convertMdxDocumentImports(text, note) {
    const imports = mdxDocumentImports(text);
    if (imports.size === 0)
        return text;
    let converted = 0;
    const resolved = new Set();
    let out = text;
    for (const [name, path] of imports) {
        const use = new RegExp(`<${name}\\s*/>|<${name}\\s*>\\s*</${name}\\s*>`, 'g');
        if (!use.test(out))
            continue;
        out = out.replace(use, () => {
            converted += 1;
            return `::snippet{name="${quoteAttr(snippetNameFromPath(path))}"}`;
        });
        resolved.add(name);
    }
    if (converted === 0)
        return text;
    // The statements for what was resolved, and only those.
    out = sourceLines(out)
        .filter((line) => {
        if (line.inCode)
            return true;
        const match = DOCUMENT_IMPORT.exec(line.text.trim());
        return !match || !resolved.has(match[1]);
    })
        .map((line) => line.text)
        .join('\n');
    note('review', 'mdx-document-import', `${converted} MDX import(s) of a Markdown file became shared blocks, and the files they name were imported alongside the pages. An import of a component — anything not ending in .md or .mdx — is still reported rather than guessed at.`, converted);
    return out;
}
/**
 * Put `import` and `export` statements inside an HTML comment.
 *
 * They cannot be deleted — that hides from the reviewer which components the
 * page depended on — and they cannot be left alone either: to Markdown,
 * `import Tabs from '@theme/Tabs'` is a PARAGRAPH, and it prints on the
 * published page.
 */
export function hideMdxStatements(text, note, platform) {
    const lines = sourceLines(text);
    const out = [];
    let statements = 0;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.inCode || !/^(import|export)\s/.test(line.text)) {
            out.push(line.text);
            continue;
        }
        // Take following lines until a blank one: an `export const x = {` block
        // spans several lines, and half of it left behind would print.
        const block = [line.text];
        while (i + 1 < lines.length && lines[i + 1].text.trim() !== '' && !lines[i + 1].inCode) {
            block.push(lines[++i].text);
        }
        statements += block.filter((entry) => /^(import|export)\s/.test(entry)).length;
        // No blank line inside the comment: a blank line ends an HTML block in
        // Markdown, and everything after it would escape the comment and print.
        out.push(`<!-- ${platform} MDX, preserved but not executed:`, ...block.map(safeInComment), '-->');
    }
    if (statements > 0) {
        note('unconverted', 'mdx-statement', 'MDX `import`/`export` statements cannot run here, and left as text they would print on the page. They are preserved inside an HTML comment so a human can see what the page depended on.', statements);
    }
    return out.join('\n');
}
/** Keep a line from closing the comment it sits in. */
export function safeInComment(line) {
    return line.replace(/--+>/g, (match) => match.replace(/>/g, '&gt;')).replace(/<!--/g, '&lt;!--');
}
/** `{/* ... *​/}` is an MDX comment. As Markdown it would print. */
export function convertMdxComments(text, note) {
    return replaceOutsideCode(text, /\{\s*\/\*([\s\S]*?)\*\/\s*\}/g, (match) => {
        note('review', 'mdx-comment', 'MDX comments became HTML comments. They stay invisible to readers, which is what they were for.');
        return `<!--${safeInComment(match[1].replace(/\n+/g, ' '))}-->`;
    });
}
/**
 * Keep a code fence's metastring, translated into the renderer's dialect.
 *
 * MEASURED, and the measurement changed: `canonicalise` used to throw a fence's
 * metastring away, so these importers wrapped such a fence in a `:::code`
 * directive to hold it. That kept the data and rendered NOTHING — the filename
 * appeared nowhere on the page, which is what a reader of a migrated site would
 * notice first.
 *
 * A fence now carries its metastring end to end, and Expressive Code reads it.
 * Measured against a real build:
 *
 * - `title="server.ts"` renders the filename frame.
 * - `{2}` marks the line.
 * - `showLineNumbers` renders nothing yet: the plugin is not enabled. It is kept
 *   on the fence anyway, where the renderer will find it the day it is.
 * - A word nothing recognises does NOT fail the build. That is what makes it
 *   safe to keep a platform's leftovers here rather than deleting them.
 *
 * So the fence is rewritten rather than wrapped, and each platform's dialect is
 * translated on the way: Docusaurus writes `{1,3}`, MkDocs writes
 * `hl_lines="1 3"`, Mintlify writes the filename bare after the language. They
 * mean the same thing to a reader and only one spelling renders.
 */
export function wrapCodeMeta(text, note, options = {}) {
    const out = [];
    /**
     * The fence we are inside, if any.
     *
     * Only the OPENING line is rewritten; everything up to the close is passed
     * through untouched. That is the whole reason this is simpler than the wrapper
     * it replaces — there is no buffer to forget to flush, which is how the first
     * version of that function ate a code block.
     */
    let inside;
    let kept = 0;
    let leftover = 0;
    for (const line of text.split('\n')) {
        const fence = line.match(/^(\s{0,3})(`{3,}|~{3,})(.*)$/);
        if (inside !== undefined) {
            if (fence && fence[2].startsWith(inside) && fence[3].trim() === '')
                inside = undefined;
            out.push(line);
            continue;
        }
        if (!fence) {
            out.push(line);
            continue;
        }
        inside = fence[2][0].repeat(3);
        const info = fence[3].trim();
        const language = info.match(/^[A-Za-z0-9+#._-]*/)?.[0] ?? '';
        let rest = info.slice(language.length).trim();
        if (rest === '') {
            out.push(line);
            continue;
        }
        const parts = [];
        const take = (pattern, build) => {
            const match = rest.match(pattern);
            if (!match)
                return;
            const part = build(match);
            if (part !== undefined)
                parts.push(part);
            rest = rest.replace(match[0], '');
        };
        // A filename, however the source spells it.
        take(/title=(?:"([^"]*)"|'([^']*)')/, (m) => `title="${quoteAttr(m[1] ?? m[2] ?? '')}"`);
        /**
         * Highlighted lines, in three dialects that mean the same thing.
         *
         * `{1,3-5}` is what Expressive Code reads, so the others are translated into
         * it. Left as written, `hl_lines="1 3"` is a word the renderer ignores and
         * the reader loses the emphasis the author put there.
         */
        take(/\{([\d\s,-]+)\}/, (m) => `{${(m[1] ?? '').replace(/\s+/g, '')}}`);
        take(/highlight=\{([^}]*)\}/, (m) => `{${(m[1] ?? '').replace(/\s+/g, '')}}`);
        take(/hl_lines=(?:"([^"]*)"|'([^']*)')/, (m) => `{${(m[1] ?? m[2] ?? '').trim().replace(/\s+/g, ',')}}`);
        // An icon is not something the renderer draws. Kept as an attribute-shaped
        // word so it is not silently deleted, and reported below with the rest.
        take(/icon=(?:"([^"]*)"|'([^']*)')/, (m) => `icon="${quoteAttr(m[1] ?? m[2] ?? '')}"`);
        for (const entry of options.drop ?? []) {
            if (!rest.includes(entry.word))
                continue;
            note('unconverted', entry.rule, entry.message);
            rest = rest.replace(entry.word, '');
        }
        for (const [word, spelling] of Object.entries(options.flags ?? {})) {
            if (!new RegExp(`(^|\\s)${word}(\\s|$)`).test(rest))
                continue;
            parts.push(spelling);
            rest = rest.replace(word, '');
        }
        /**
         * A bare filename after the language, which Mintlify writes:
         *
         * ```` ```javascript App.js ````
         *
         * Only when it looks like a filename and nothing else claimed it. Guessing
         * more widely would turn a stray word into a header on somebody's code.
         */
        if (options.bracketTitle && !parts.some((part) => part.startsWith('title='))) {
            const label = rest.trim().match(/^\[([^\]]+)\]$/);
            if (label) {
                parts.unshift(`title="${quoteAttr(label[1])}"`);
                rest = '';
            }
        }
        if (options.bareTitle && !parts.some((part) => part.startsWith('title='))) {
            const bare = rest.trim();
            if (/^[\w./-]+\.[A-Za-z0-9]+$/.test(bare)) {
                parts.unshift(`title="${quoteAttr(bare)}"`);
                rest = '';
            }
        }
        if (rest.trim().length > 0) {
            // Kept verbatim on the fence. A word the renderer does not recognise is
            // ignored rather than fatal — measured — so keeping it costs nothing and
            // deleting it loses whatever the author meant by it.
            parts.push(rest.trim());
            leftover += 1;
        }
        if (parts.length === 0) {
            out.push(`${fence[1]}${fence[2]}${language}`);
            continue;
        }
        kept += 1;
        out.push(`${fence[1]}${fence[2]}${language} ${parts.join(' ')}`);
    }
    if (kept > 0) {
        note('review', 'code-meta', 'A code fence carried a metastring — a filename, highlighted lines, line numbers. It is kept on the fence, and the filename and the highlights render. Line numbers do not yet: that renderer plugin is not enabled.', kept);
    }
    if (leftover > 0) {
        note('unconverted', 'code-meta-unknown', 'A code block carried metastring options with no equivalent here. They are left on the fence rather than deleted — the renderer ignores what it does not recognise — so nothing is lost, but nothing renders from them either.', leftover);
    }
    return out.join('\n');
}
/**
 * Every remaining JSX component becomes a directive.
 *
 * The alternative — leaving the JSX — was rejected after checking what happens:
 * raw `<ResponseField>` survives our round trip, but it renders as an unknown
 * HTML element, so the reader gets the children with no label and no styling. A
 * directive keeps the name, keeps every attribute, keeps the children as
 * Markdown, renders its content, and starts rendering properly the day the
 * block exists. That is what `:::embed` already does for GitBook.
 *
 * An INLINE component is the exception. Turning `<Tooltip>word</Tooltip>` in the
 * middle of a sentence into a block would split the sentence in two, so those
 * are left as written and reported.
 */
export function convertUnmodelledComponents(text, note) {
    return replaceJsxElements(text, (name) => /^[A-Z]/.test(name), (element) => {
        const kebab = kebabCase(element.name);
        if (!isOwnLine(text, element)) {
            note('unconverted', 'component-inline', `Inline components such as <${element.name}> were left as written. Turning one into a block would split the sentence it sits in.`);
            return undefined;
        }
        note('unconverted', `component-${kebab}`, `<${element.name}> has no block in our schema. It became a \`:::${kebab}\` directive: the attributes and the content are preserved, it renders its content, and it will render properly the day the block exists.`);
        return directiveFor(element, kebab, note);
    });
}
/** One JSX element as a directive, with its children converted recursively. */
export function directiveFor(element, name, note) {
    const attributes = Object.entries(element.attributes).map(([key, value]) => `${kebabCase(key)}="${quoteAttr(value)}"`);
    for (const [key, expression] of Object.entries(element.expressions)) {
        attributes.push(`${kebabCase(key)}="${quoteAttr(expression)}"`);
        note('unconverted', 'component-expression-attribute', 'A component attribute was a JavaScript expression. The expression TEXT was kept as the attribute value, because we cannot evaluate it — a human has to decide what it should say.');
    }
    const body = element.selfClosing
        ? ''
        : convertUnmodelledComponents(dedent(element.children), note);
    const fence = ':'.repeat(fenceWidth(body));
    const attrs = attributes.length > 0 ? `{${attributes.join(' ')}}` : '';
    return body.trim().length > 0
        ? `${fence}${name}${attrs}\n${body}\n${fence}`
        : `${fence}${name}${attrs}\n${fence}`;
}
/** Is the element alone on its lines, or inside a sentence? */
export function isOwnLine(text, element) {
    const before = text.slice(text.lastIndexOf('\n', element.start - 1) + 1, element.start);
    const afterEnd = text.indexOf('\n', element.end);
    const after = text.slice(element.end, afterEnd === -1 ? text.length : afterEnd);
    return before.trim() === '' && after.trim() === '';
}
export function kebabCase(name) {
    return name
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/[^A-Za-z0-9]+/g, '-')
        .toLowerCase()
        .replace(/^-|-$/g, '');
}
/** `<details><summary>` onto `expandable`, which is the same thing. */
export function convertDetails(text, note) {
    return replaceJsxElements(text, (name) => name === 'details' || name === 'Details', (element) => {
        const summary = findSummary(element.children);
        const title = summary ? stripTags(summary.children) : 'Details';
        // Convert what is inside before measuring the fence, or a component in
        // the body becomes a directive of the same width and closes this one.
        const body = convertUnmodelledComponents(dedent(summary ? element.children.replace(summary.raw, '') : element.children), note);
        note('review', 'details', '`<details>` became `:::expandable`, with the `<summary>` as its title. Any markup inside the summary became plain text: our title is an attribute, not content.');
        const fence = ':'.repeat(fenceWidth(body));
        return `${fence}expandable{title="${quoteAttr(title)}"}\n${body}\n${fence}`;
    });
}
function findSummary(children) {
    const [first] = replaceJsxElementsToList(children, (name) => name.toLowerCase() === 'summary');
    return first;
}
/** Small helper so `convertDetails` does not need the scanner's full surface. */
function replaceJsxElementsToList(text, accept) {
    const found = [];
    replaceJsxElements(text, accept, (element) => {
        found.push(element);
        return undefined;
    });
    return found;
}
export function stripTags(text) {
    return text
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
//# sourceMappingURL=import-mdx.js.map